// search-layer/stores/search.query-store.ts

import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { SearchService } from '../services/search.service'
import type { SearchParams, SearchResultItem } from '../types/search'

const service = new SearchService()

export const useSearchQueryStore = () => {
  const queryClient = useQueryClient()

  // ===== QUERY KEY FACTORY =====
  const queryKeys = {
    all: (locationId?: string, groupId?: string) => ['search', locationId, groupId] as const,
    results: (query: string, locationId?: string, groupId?: string, type?: string) =>
      ['search', locationId, groupId, 'results', query, type] as const,
    infinite: (
      query: string,
      locationId?: string,
      groupId?: string,
      channelId?: string,
      type?: string
    ) => ['search', locationId, groupId, channelId, 'infinite', query, type] as const,
  }

  // ===== QUERY CREATION FUNCTIONS =====

  /**
   * @param query - Search query string
   * @param locationId - Location ID (required)
   * @param groupId - Optional group ID for group-specific search
   * @param type - Optional type filter (post/comment/all)
   * @returns TanStack Query object with search results
   */
  const createSearchQuery = (
    query: MaybeRefOrGetter<string>,
    locationId: MaybeRefOrGetter<string | undefined>,
    groupId: MaybeRefOrGetter<string | undefined>,
    type: MaybeRefOrGetter<'post' | 'comment' | 'all' | undefined> = undefined
  ) => {
    return useQuery({
      queryKey: computed(() => {
        const queryValue = toValue(query)
        const locationIdValue = toValue(locationId)
        const groupIdValue = toValue(groupId)
        const typeValue = toValue(type)
        return queryKeys.results(queryValue, locationIdValue, groupIdValue, typeValue)
      }),
      queryFn: async () => {
        const queryValue = toValue(query)
        const locationIdValue = toValue(locationId)
        const groupIdValue = toValue(groupId)
        const typeValue = toValue(type)

        if (!queryValue || queryValue.trim().length === 0) {
          return []
        }

        if (!locationIdValue) {
          throw new Error('locationId is required for search')
        }

        const searchParams: SearchParams = {
          query: queryValue,
          locationId: locationIdValue,
          groupId: groupIdValue,
          type: typeValue,
        }

        return service.search(searchParams)
      },
      enabled: computed(() => {
        const queryValue = toValue(query)
        const locationIdValue = toValue(locationId)
        return !!queryValue && queryValue.trim().length > 0 && !!locationIdValue
      }),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    })
  }

  /**
   * @param query - Search query string
   * @param locationId - Location ID (required)
   * @param groupId - Optional group ID for group-specific search
   * @param channelId - Optional channel ID for channel-specific search
   * @param type - Optional type filter (post/comment/all)
   * @returns TanStack Infinite Query object with paginated search results
   */
  const createInfiniteSearchQuery = (
    query: MaybeRefOrGetter<string>,
    locationId: MaybeRefOrGetter<string | undefined>,
    groupId: MaybeRefOrGetter<string | undefined> = undefined,
    channelId: MaybeRefOrGetter<string | undefined> = undefined,
    type: MaybeRefOrGetter<'post' | 'comment' | 'all' | undefined> = undefined
  ) => {
    return useInfiniteQuery({
      queryKey: computed(() => {
        const queryValue = toValue(query)
        const locationIdValue = toValue(locationId)
        const groupIdValue = toValue(groupId)
        const channelIdValue = toValue(channelId)
        const typeValue = toValue(type)
        return queryKeys.infinite(
          queryValue,
          locationIdValue,
          groupIdValue,
          channelIdValue,
          typeValue
        )
      }),
      queryFn: async ({ pageParam }) => {
        const queryValue = toValue(query)
        const locationIdValue = toValue(locationId)
        const groupIdValue = toValue(groupId)
        const channelIdValue = toValue(channelId)
        const typeValue = toValue(type)

        if (!queryValue || queryValue.trim().length === 0) {
          return []
        }

        if (!locationIdValue) {
          throw new Error('locationId is required for search')
        }

        const searchParams: SearchParams = {
          query: queryValue,
          locationId: locationIdValue,
          groupId: groupIdValue,
          channelId: channelIdValue,
          type: typeValue,
          token: pageParam as string | undefined,
        }

        return service.search(searchParams)
      },
      getNextPageParam: (lastPage: SearchResultItem[]) => {
        if (!lastPage || lastPage.length === 0) {
          return undefined
        }

        const lastItem = lastPage[lastPage.length - 1]
        return lastItem?.token
      },
      initialPageParam: undefined,
      enabled: computed(() => {
        const queryValue = toValue(query)
        const locationIdValue = toValue(locationId)
        return !!queryValue && queryValue.trim().length > 0 && !!locationIdValue
      }),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    })
  }

  // ===== CACHE MANAGEMENT =====

  const invalidateSearchQueries = (locationId?: string, groupId?: string) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.all(locationId, groupId),
    })
  }

  const clearSearchCache = () => {
    queryClient.removeQueries({
      queryKey: ['search'],
    })
  }

  return {
    // Query creation functions
    createSearchQuery,
    createInfiniteSearchQuery,

    // Cache management
    invalidateSearchQueries,
    clearSearchCache,

    // Query keys for manual operations
    queryKeys,
  }
}
